import { modifier } from 'ember-modifier';
import isRemovedString from 'garaje/helpers/is-removed-string';
import removedString from 'garaje/helpers/removed-string';

type FormControlElement = HTMLInputElement | HTMLTextAreaElement;

const FORM_CONTROL_SELECTOR = 'input, textarea';
const REMOVED_ARIA_LABEL = 'Field value was deleted';

function isFormControl(element: Element): element is FormControlElement {
  return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;
}

function findFormControls(root: Element): FormControlElement[] {
  const controls: FormControlElement[] = [];
  if (isFormControl(root)) controls.push(root);
  root.querySelectorAll<FormControlElement>(FORM_CONTROL_SELECTOR).forEach((control) => {
    controls.push(control);
  });
  return controls;
}

interface HandleRemovedStringSignature {
  Args: {
    Positional: [value?: string | null];
  };
  Element: HTMLElement;
}

export default modifier<HandleRemovedStringSignature>((element, [value]) => {
  if (!isRemovedString(value)) return;

  const controls = findFormControls(element);

  if (controls.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(
      `{{handle-removed-string}} expected a single form control but found ${controls.length}. Only the first will be modified.`,
      element,
    );
  }

  if (controls.length === 0) {
    // eslint-disable-next-line no-console
    console.warn(`{{handle-removed-string}} expected at least one form control but found none.`, element);
    return;
  }

  const control = controls[0];
  if (!control) return;

  const previousValue = control.value;
  const previousDisabled = control.disabled;
  const previousAriaLabel = control.getAttribute('aria-label');

  control.value = removedString(value);
  control.disabled = true;

  const contextLabel = previousAriaLabel ?? '';
  control.setAttribute('aria-label', contextLabel ? `${contextLabel}: ${REMOVED_ARIA_LABEL}` : REMOVED_ARIA_LABEL);

  return () => {
    control.value = previousValue;
    control.disabled = previousDisabled;
    if (previousAriaLabel === null) {
      control.removeAttribute('aria-label');
    } else {
      control.setAttribute('aria-label', previousAriaLabel);
    }
  };
});
